import { CSSProperties } from "react";

const LETTERS = ["M", "A", "K", "E", "C", "H", "A", "N", "G", "E"];

/**
 * Canonical MAKECHANGE framework (locked). Duplicate letters carry distinct
 * pillars, so this is keyed by index — never by letter.
 */
const WORDS = [
  "Momentum",
  "Authority",
  "Kingdom-Focus",
  "Equity",
  "Clarity",
  "Healing",
  "Action",
  "Navigation",
  "Growth",
  "Empowerment",
] as const;

type Variant = "vertical" | "diagonal" | "scatter" | "ticker" | "watermark";

type Props = {
  variant?: Variant;
  className?: string;
  /** 0..1 — overall opacity multiplier (default 1) */
  intensity?: number;
  /** show small caption word under each letter */
  withWords?: boolean;
  side?: "left" | "right";
  /** blur amount in px applied to the motif layer (default 0) */
  blur?: number;
  /**
   * Optional screen-reader label. The motif itself is always `aria-hidden`
   * (a screen reader spelling out "M A K E C H A N G E" letter by letter is
   * noise, not information). When a section leans on the motif to carry
   * meaning, pass a short sentence here and it is announced once, in place of
   * the decorative glyphs.
   */
  label?: string;
  /**
   * Multiplies every fluid size in the motif. Lets a dense section dial the
   * signature down without introducing a second set of breakpoints.
   */
  scale?: number;
};

/**
 * Fluid type ramps for the motif.
 *
 * The motif is rendered as live text, not an image, so it stays perfectly
 * crisp at any DPR and costs zero image bytes — the "smallest suitable size
 * per device" is achieved by scaling glyphs with `clamp()` against the
 * viewport instead of shipping per-breakpoint raster assets.
 *
 * Each ramp is clamp(min @ 320px, fluid, max @ 1536px) and is multiplied by
 * the `--mc-motif-scale` custom property so callers can tune density.
 */
const RAMP = {
  watermark: "clamp(3.5rem, 17vw, 15rem)",
  ticker: "clamp(1.25rem, 5vw, 4rem)",
  diagonal: "clamp(1.5rem, 7vw, 6rem)",
  scatter: "clamp(1.75rem, 4vw, 3.75rem)",
  vertical: "clamp(0.75rem, 1.1vw, 1.25rem)",
} as const;

/** `calc()` wrapper that folds the caller's scale into a ramp. */
const sized = (ramp: string): CSSProperties => ({
  fontSize: `calc(${ramp} * var(--mc-motif-scale, 1))`,
  lineHeight: 1,
});

export const MakeChangeMotif = ({
  variant = "vertical",
  className = "",
  intensity = 1,
  withWords = false,
  side = "right",
  blur = 0,
  scale = 1,
  label,
}: Props) =>
  label ? (
    <>
      <span className="sr-only">{label}</span>
      <MotifLayer
        variant={variant}
        className={className}
        intensity={intensity}
        withWords={withWords}
        side={side}
        blur={blur}
        scale={scale}
      />
    </>
  ) : (
    <MotifLayer
      variant={variant}
      className={className}
      intensity={intensity}
      withWords={withWords}
      side={side}
      blur={blur}
      scale={scale}
    />
  );

/** Decorative glyph layer. Always hidden from assistive technology. */
const MotifLayer = ({
  variant = "vertical",
  className = "",
  intensity = 1,
  withWords = false,
  side = "right",
  blur = 0,
  scale = 1,
}: Omit<Props, "label">) => {
  const baseStyle: CSSProperties = {
    opacity: intensity,
    // Blur is a viewport-relative amount too, so the softness reads the same
    // on a 390px phone as on a 2560px display.
    filter: blur > 0 ? `blur(calc(${blur}px * var(--mc-motif-scale, 1)))` : undefined,
    ["--mc-motif-scale" as string]: scale,
  };

  if (variant === "watermark") {
    return (
      <div
        aria-hidden
        className={`pointer-events-none select-none absolute inset-0 z-0 overflow-hidden flex items-center justify-center ${className}`}
        style={baseStyle}
      >
        <div
          className="makechange-strip whitespace-nowrap"
          style={sized(RAMP.watermark)}
        >
          MAKECHANGE
        </div>
      </div>
    );
  }

  if (variant === "ticker") {
    // Two marquee copies are enough to cover a phone viewport; the 3rd and 4th
    // only mount from `md` up, where the strip is wide enough to expose a seam.
    return (
      <div
        aria-hidden
        className={`pointer-events-none select-none absolute inset-x-0 z-0 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)] ${className}`}
        style={baseStyle}
      >
        <div className="flex gap-6 md:gap-12 animate-[mc-marquee_70s_linear_infinite] motion-reduce:animate-none whitespace-nowrap will-change-transform">
          {Array.from({ length: 4 }).map((_, r) => (
            <div
              key={r}
              className={`flex items-center gap-6 md:gap-12 px-4 md:px-6 ${
                r > 1 ? "hidden md:flex" : ""
              }`}
            >
              {LETTERS.map((l, i) => {
                // Highlight every 3rd letter in gold for rhythm
                const accent = i % 3 === 0;
                return (
                  <span key={`${r}-${i}`} className="flex items-center gap-6 md:gap-12">
                    <span
                      className={`font-display font-bold tracking-[0.25em] md:tracking-[0.35em] ${
                        accent ? "text-gold/25" : "text-white/15"
                      }`}
                      style={sized(RAMP.ticker)}
                    >
                      {l}
                    </span>
                    <span className="h-1.5 w-1.5 md:h-2 md:w-2 rounded-full bg-gold/40" />
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "diagonal") {
    return (
      <div
        aria-hidden
        className={`pointer-events-none select-none absolute inset-0 z-0 overflow-hidden ${className}`}
        style={baseStyle}
      >
        <div className="absolute -top-6 -left-6 md:-top-10 md:-left-10 rotate-[-18deg] origin-top-left flex flex-col gap-3 md:gap-6">
          {LETTERS.map((l, i) => (
            <span
              key={i}
              className="font-display font-bold text-white/[0.05]"
              style={{
                ...sized(RAMP.diagonal),
                // Stagger scales with the glyph so the cascade angle is stable.
                marginLeft: `calc(${i * 0.6}rem * var(--mc-motif-scale, 1))`,
              }}
            >
              {l}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "scatter") {
    // One DOM pass instead of a duplicated mobile/desktop tree: the first four
    // letters always render, the rest reveal from `md` up. Halves the nodes a
    // phone has to lay out for a purely decorative layer.
    // Positions are tuned so the glyphs hug the outer gutters and the top/
    // bottom bands — the middle column (where the eyebrow, H1, subhead and CTA
    // stack live) is deliberately left empty so nothing sits behind copy.
    // `mobile: false` entries only mount from `md` up, where there is real
    // horizontal room beside the text column.
    const positions = [
      { top: "4%", left: "2%", mobile: true },
      { top: "30%", left: "90%", mobile: true },
      { top: "68%", left: "1%", mobile: true },
      { top: "92%", left: "88%", mobile: true },
      { top: "48%", left: "8%", mobile: false },
      { top: "12%", left: "62%", mobile: false },
      { top: "78%", left: "70%", mobile: false },
    ];
    return (
      <div
        aria-hidden
        className={`pointer-events-none select-none absolute inset-0 z-0 overflow-hidden ${className}`}
        style={baseStyle}
      >
        {positions.map((pos, i) => (
          <span
            key={i}
            className={`absolute font-display font-bold text-white/[0.045] md:text-white/[0.06] ${
              pos.mobile ? "" : "hidden md:inline"
            }`}
            style={{ top: pos.top, left: pos.left, ...sized(RAMP.scatter) }}
          >
            {LETTERS[i]}
          </span>
        ))}
      </div>
    );
  }

  // vertical (default)
  const sideClass = side === "left" ? "left-3 md:left-6" : "right-3 md:right-6";
  return (
    <div
      aria-hidden
      className={`pointer-events-none select-none absolute top-0 bottom-0 z-0 ${sideClass} hidden sm:flex flex-col items-center justify-center gap-2 md:gap-5 ${className}`}
      style={baseStyle}
    >
      {LETTERS.map((l, i) => (
        <div key={i} className="flex flex-col items-center">
          <span
            className="font-display font-bold text-gold/20 tracking-widest"
            style={sized(RAMP.vertical)}
          >
            {l}
          </span>
          {withWords && (
            <span className="hidden md:inline text-[9px] uppercase tracking-[0.25em] text-silver/30">
              {WORDS[i]}
            </span>
          )}
          {i < LETTERS.length - 1 && (
            <span className="mt-1.5 md:mt-3 h-2 md:h-3 w-px bg-gold/15" />
          )}
        </div>
      ))}
    </div>
  );
};
